/**
 * Pure logic for the "Map prompt template" modal (Figma Prompt-Mapper):
 * build a template from an example record by replacing dataset-column values
 * and custom-field texts with `{{field}}` placeholders, parse templates into
 * renderable segments, and restore text when a field is unmapped.
 */
import { getFieldValue, formatOriginalColumnPreview } from '@/features/datasets/utils/nestedFieldValue';

const PLACEHOLDER_REGEX = /\{\{\s*([\w.-]+)\s*\}\}/g;
const MAX_FIELD_NAME_LENGTH = 50;
/** Values shorter than this are too noisy to auto-map ("a", "1", …). */
const MIN_MAPPABLE_VALUE_LENGTH = 2;

/** Placeholder names share the backend charset — other column names can't map. */
export function isMappableFieldName(name) {
    return typeof name === 'string' && /^[\w.-]+$/.test(name);
}

/** Sanitized `{{field}}`-safe name from free text (mirrors PromptTemplateStep). */
export function sanitizeFieldName(text) {
    const trimmed = (text || '').trim();
    if (!trimmed) return null;
    return (
        trimmed
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '')
            .substring(0, MAX_FIELD_NAME_LENGTH) || null
    );
}

export function ensureUniqueFieldName(fieldName, existingNames) {
    const taken = new Set(existingNames || []);
    if (!taken.has(fieldName)) return fieldName;
    let counter = 1;
    while (taken.has(`${fieldName}_${counter}`)) counter += 1;
    return `${fieldName}_${counter}`;
}

/**
 * String values of `columns` for one record (dot paths supported), formatted
 * exactly like the preview cards so mapping matches what the user sees.
 * Empty / missing values are dropped.
 */
export function recordValuesForColumns(record, columns) {
    const values = {};
    (columns || []).forEach((column) => {
        const raw = getFieldValue(record, column);
        if (raw === undefined || raw === null) return;
        const text = formatOriginalColumnPreview(raw);
        if (!text || !text.trim()) return;
        values[column] = text;
    });
    return values;
}

/** Parse a template into `{type:'text'|'field'}` segments for rendering. */
export function parseTemplateSegments(template) {
    const segments = [];
    const text = template || '';
    let lastIndex = 0;
    for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', text: text.substring(lastIndex, match.index) });
        }
        segments.push({ type: 'field', name: match[1] });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        segments.push({ type: 'text', text: text.substring(lastIndex) });
    }
    return segments;
}

function segmentsToTemplate(segments) {
    return segments
        .map((segment) => (segment.type === 'field' ? `{{${segment.name}}}` : segment.text))
        .join('');
}

/** Distinct field names in first-occurrence order. */
export function fieldsInTemplate(template) {
    return [...new Set([...(template || '').matchAll(PLACEHOLDER_REGEX)].map((m) => m[1]))];
}

/** `{ fieldName: occurrenceCount }` for the panel badges. */
export function fieldOccurrences(template) {
    const counts = {};
    for (const match of (template || '').matchAll(PLACEHOLDER_REGEX)) {
        counts[match[1]] = (counts[match[1]] || 0) + 1;
    }
    return counts;
}

/** "N fields mapped" — distinct dynamic fields in the template. */
export function mappedFieldsCount(template) {
    return fieldsInTemplate(template).length;
}

/**
 * Split every text segment on `value`, inserting `{{fieldName}}` segments.
 * Existing field segments are never touched, so a shorter value can't match
 * inside an already-mapped placeholder.
 */
function splitSegmentsOnValue(segments, value, fieldName) {
    let matched = false;
    const next = [];
    for (const segment of segments) {
        if (segment.type !== 'text' || !segment.text.includes(value)) {
            next.push(segment);
            continue;
        }
        matched = true;
        const parts = segment.text.split(value);
        parts.forEach((part, index) => {
            if (part) next.push({ type: 'text', text: part });
            if (index < parts.length - 1) next.push({ type: 'field', name: fieldName });
        });
    }
    return { segments: next, matched };
}

/**
 * Build a template from an example text by replacing the record's column
 * values and the custom fields' original texts with placeholders. Longer
 * values are applied first so a value that is a substring of another can't
 * break the longer match. Returns the template plus which fields matched.
 *
 * @param exampleText  raw value of the selected example field for one record
 * @param recordValues `{ column: stringValue }` from recordValuesForColumns
 * @param customFields `{ fieldName: originalText }` user-defined highlights
 */
export function buildMappedTemplate(exampleText, recordValues = {}, customFields = {}) {
    let segments = [{ type: 'text', text: exampleText || '' }];

    const candidates = [
        ...Object.entries(recordValues).map(([name, value]) => ({ name, value, kind: 'column' })),
        ...Object.entries(customFields).map(([name, value]) => ({ name, value, kind: 'custom' })),
    ].filter(
        ({ name, value }) =>
            isMappableFieldName(name) &&
            typeof value === 'string' &&
            value.trim().length >= MIN_MAPPABLE_VALUE_LENGTH
    );
    candidates.sort((a, b) => b.value.length - a.value.length);

    const mappedColumns = [];
    const mappedCustomFields = [];
    for (const { name, value, kind } of candidates) {
        const result = splitSegmentsOnValue(segments, value, name);
        segments = result.segments;
        if (result.matched) {
            (kind === 'column' ? mappedColumns : mappedCustomFields).push(name);
        }
    }

    return { template: segmentsToTemplate(segments), mappedColumns, mappedCustomFields };
}

/**
 * Map `value` occurrences inside an existing template's text (placeholders
 * untouched) to `{{name}}` — used when a custom field is added from a text
 * selection or a mapped-value column is toggled on.
 */
export function applyValueToTemplate(template, name, value) {
    if (!isMappableFieldName(name)) return template;
    if (typeof value !== 'string' || value.trim().length < MIN_MAPPABLE_VALUE_LENGTH) {
        return template;
    }
    const { segments, matched } = splitSegmentsOnValue(
        parseTemplateSegments(template),
        value,
        name
    );
    return matched ? segmentsToTemplate(segments) : template;
}

/**
 * Align a template against a concrete text: literal template segments are
 * anchored left-to-right in `text` (leftmost match), and the gap between two
 * anchors is the text captured by the `{{field}}`(s) sitting between them.
 * A trailing field captures the rest of the text.
 *
 * This answers "what does each placeholder stand for in this record" without
 * relying on the fields' stored values — which is what the original-text
 * view and Shuffle need for custom fields whose stored text is empty or
 * comes from another record.
 *
 * @returns segments `{type:'text',text} | {type:'field',name,text}` covering
 *   the whole `text`, or `null` when a literal anchor cannot be found in
 *   order (the template does not fit this text). Gaps with no placeholder
 *   between anchors become plain text segments; adjacent placeholders assign
 *   the whole gap to the first one (the rest capture '').
 */
export function alignTemplateToText(template, text) {
    const target = text || '';
    const out = [];
    let pos = 0;
    let pending = [];

    const flushGap = (gap) => {
        if (pending.length === 0) {
            if (gap) out.push({ type: 'text', text: gap });
            return;
        }
        pending.forEach((name, index) => {
            out.push({ type: 'field', name, text: index === 0 ? gap : '' });
        });
        pending = [];
    };

    for (const segment of parseTemplateSegments(template)) {
        if (segment.type === 'field') {
            pending.push(segment.name);
            continue;
        }
        const index = target.indexOf(segment.text, pos);
        if (index === -1) return null;
        flushGap(target.substring(pos, index));
        out.push({ type: 'text', text: segment.text });
        pos = index + segment.text.length;
    }
    flushGap(target.substring(pos));
    return out;
}

function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Remove every `{{name}}` occurrence, restoring `replacementText` in place
 * (the record's column value, or a custom field's original text).
 */
export function unmapField(template, name, replacementText = '') {
    const pattern = new RegExp(`\\{\\{\\s*${escapeRegExp(name)}\\s*\\}\\}`, 'g');
    return (template || '').replace(pattern, replacementText ?? '');
}

/**
 * Insert `{{name}}` into a template at a character offset, keeping the
 * offset from landing inside an existing placeholder (snaps to its end).
 */
export function insertFieldAtOffset(template, name, offset) {
    const text = template || '';
    let safeOffset = Math.max(0, Math.min(offset ?? text.length, text.length));
    for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
        const start = match.index;
        const end = match.index + match[0].length;
        if (safeOffset > start && safeOffset < end) {
            safeOffset = end;
            break;
        }
    }
    return `${text.substring(0, safeOffset)}{{${name}}}${text.substring(safeOffset)}`;
}

/**
 * Aggregate per-record batch extraction results (POST
 * /preview-field-extraction-batch) into failure counts for the Save-time
 * validation. A record fails the primary field when `extracted_example` is
 * empty, and fails any other mapped field that is missing/empty in
 * `extracted_fields` (the backend omits fields it could not extract).
 *
 * @param results      `[{ extracted_example, extracted_fields }]` per record
 * @param mappedFields distinct field names in the template (fieldsInTemplate)
 * @param primaryField the field sent as example_field_name
 * @returns `{ checkedRecords, failedRecords, fieldCounts: {name: count} }`
 */
export function collectExtractionFailures(results, mappedFields, primaryField) {
    const fieldCounts = {};
    let failedRecords = 0;
    const list = results || [];
    for (const result of list) {
        const extractedFields = result?.extracted_fields || {};
        let recordFailed = false;
        for (const name of mappedFields || []) {
            const value =
                name === primaryField ? result?.extracted_example : extractedFields[name];
            const missing =
                value === undefined ||
                value === null ||
                (typeof value === 'string' && !value.trim());
            if (missing) {
                fieldCounts[name] = (fieldCounts[name] || 0) + 1;
                recordFailed = true;
            }
        }
        if (recordFailed) failedRecords += 1;
    }
    return { checkedRecords: list.length, failedRecords, fieldCounts };
}

/**
 * True when the raw record value reads as free text (Tt icon) rather than a
 * structured value — numbers, booleans, objects, and numeric/ISO-date-like
 * strings get the `{..}` icon in the Available fields panel.
 */
export function isTextualValue(rawValue) {
    if (typeof rawValue !== 'string') return false;
    const trimmed = rawValue.trim();
    if (!trimmed) return false;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return false;
    if (/^\d{4}-\d{2}-\d{2}([T ].*)?$/.test(trimmed)) return false;
    if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
        return false;
    }
    return true;
}
